<template>
    <div class="w-full min-h-screen flex flex-col">
        <MainNav />
        <!-- Hero Section -->
        <section class="w-full relative">
            <img 
                src="/image/83islands_batch.jpg" 
                alt="83 Islands Barrel Warehouse" 
                class="w-full h-[800px] object-cover object-center"
            />
            
            <!-- Text Overlay -->
            <div class="mx-auto absolute mt-36 inset-0 z-20 w-full p-4 lg:p-36 flex flex-col items-center lg:items-start">
                <h1 class="text-white font-maax text-4xl uppercase lg:text-6xl font-bold text-center lg:text-left max-w-3xl leading-tight drop-shadow-[0_5px_5px_rgba(0,0,0,0.3)]">
                    Batch Information
                </h1>
                <p class="mt-4 text-white text-xl lg:text-2xl max-w-2xl mx-auto lg:mx-0 text-center lg:text-left font-maax drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
                    Discover the detailed information about our carefully crafted batches of exceptional spirits.
                </p>
            </div>

            <!-- Double Wave -->
            <div class="absolute -bottom-1 left-0 right-0 w-full overflow-hidden">
                <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="w-full h-20 text-white">
                    <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" 
                          fill="currentColor" class="opacity-25"/>
                    <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" 
                          fill="currentColor"/>
                </svg>
            </div>
        </section>

        <main class="flex-grow w-full bg-white py-12">
            <div class="container mx-auto p-4 lg:p-8">
                <!-- Update Warning -->
                <div class="mb-8 p-6 bg-amber-50 border-l-4 border-amber-500 rounded">
                    <p class="font-maax text-base text-amber-800 leading-relaxed font-semibold">
                        This page is being updated shortly to use our new batch tracking system. Some information may be incomplete during the transition.
                    </p>
                </div>

                <!-- Information Message -->
                <div class="mb-8 p-6 bg-gray-50 border-l-4 border-black rounded">
                    <p class="font-maax text-base text-gray-800 leading-relaxed mb-3">
                        We craft a diverse range of spirits, all produced under the strict standards of <strong>ISO 22000</strong>.
                    </p>
                    <p class="font-maax text-base text-gray-800 leading-relaxed mb-3">
                        Every bottle carries its own batch number, ensuring complete traceability. From the field where the cane was grown to the moment it's sealed at the distillery.
                    </p>
                    <p class="font-maax text-base text-gray-800 leading-relaxed mb-3">
                        Here, we share detailed information about our rum production only.
                    </p>
                    <p class="font-maax text-base text-gray-800 leading-relaxed">
                        If you'd like to learn more about another spirit or specific batch, please reach out to us directly at <a href="mailto:contact@83islands.com" class="text-black font-semibold underline hover:text-gray-700">contact@83islands.com</a> — we'll be glad to assist.
                    </p>
                </div>

                <!-- Filter Buttons -->
                <div class="mb-8">
                    <div class="flex flex-wrap gap-3 items-center">
                        <span class="font-maax text-lg font-bold text-gray-900 mr-2">Filter by Batch:</span>
                        <button
                            v-for="batch in batches"
                            :key="batch.id"
                            @click="toggleBatch(batch.id)"
                            :class="[
                                'px-4 py-2 rounded border-2 font-maax text-sm font-semibold transition-all',
                                selectedBatches.includes(batch.id)
                                    ? 'bg-black text-white border-black'
                                    : 'bg-white text-black border-black hover:bg-gray-100'
                            ]"
                        >
                            {{ batch.id }}
                        </button>
                        <button
                            v-if="selectedBatches.length > 0"
                            @click="clearSelection"
                            class="px-4 py-2 rounded border-2 border-gray-300 bg-white text-gray-700 font-maax text-sm font-semibold hover:bg-gray-50 transition-all"
                        >
                            Clear All
                        </button>
                    </div>
                </div>

                <!-- Batches Grid -->
                <div 
                    :class="[
                        'grid gap-8',
                        selectedBatches.length === 0 
                            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                            : selectedBatches.length === 1
                            ? 'grid-cols-1'
                            : selectedBatches.length === 2
                            ? 'grid-cols-1 lg:grid-cols-2'
                            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                    ]"
                >
                    <div 
                        v-for="batch in filteredBatches" 
                        :key="batch.id" 
                        :id="batch.id"
                        class="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    >
                        <!-- Card Header -->
                        <div class="p-6 border-b border-gray-200">
                            <p class="text-3xl font-bold font-maax text-gray-900 mb-3">{{ batch.id }}</p>
                            <h2 class="text-2xl font-bold font-maax text-gray-900 mb-3">{{ batch.name }}</h2>
                            <p class="text-base text-gray-600 font-maax">{{ batch.subtitle }}</p>
                        </div>

                        <!-- Card Body -->
                        <div class="p-6 space-y-6">
                            <!-- Blend Section -->
                            <div v-if="batch.blend">
                                <h3 class="text-lg font-bold font-maax text-gray-800 mb-2">{{ batch.blend.title }}</h3>
                                <p class="text-sm text-gray-600 font-maax leading-relaxed">{{ batch.blend.description }}</p>
                            </div>
                            
                            <!-- Resting Section -->
                            <div v-if="batch.resting">
                                <h3 class="text-lg font-bold font-maax text-gray-800 mb-2">{{ batch.resting.title }}</h3>
                                <p class="text-sm text-gray-600 font-maax leading-relaxed">{{ batch.resting.description }}</p>
                            </div>

                            <!-- Barrels Section -->
                            <div v-if="batch.barrels">
                                <h3 class="text-lg font-bold font-maax text-gray-800 mb-2">{{ batch.barrels.title }}</h3>
                                <p class="text-sm text-gray-600 font-maax leading-relaxed mb-4">{{ batch.barrels.description }}</p>
                                
                                <!-- Barrels Table (simplified for card view) -->
                                <div v-if="batch.barrels.table && batch.barrels.table.length" class="mb-4">
                                    <div class="overflow-x-auto">
                                        <table class="min-w-full text-xs divide-y divide-gray-200">
                                            <thead class="bg-gray-50">
                                                <tr>
                                                    <th class="px-2 py-2 text-left text-xs font-semibold text-gray-900 font-maax">Barrel</th>
                                                    <th class="px-2 py-2 text-left text-xs font-semibold text-gray-900 font-maax">Age</th>
                                                    <th class="px-2 py-2 text-left text-xs font-semibold text-gray-900 font-maax">%</th>
                                                </tr>
                                            </thead>
                                            <tbody class="bg-white divide-y divide-gray-200">
                                                <tr v-for="row in batch.barrels.table" :key="row.batch">
                                                    <td class="px-2 py-2 text-xs text-gray-900 font-maax">{{ row.batch }}</td>
                                                    <td class="px-2 py-2 text-xs text-gray-600 font-maax">{{ row.age }}</td>
                                                    <td class="px-2 py-2 text-xs text-gray-600 font-maax">{{ row.blendPercentage }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                
                                <p v-if="batch.barrels.stats" class="text-xs text-gray-600 font-maax italic">{{ batch.barrels.stats }}</p>
                            </div>

                            <!-- Volume Section -->
                            <div v-if="batch.volume">
                                <h3 class="text-lg font-bold font-maax text-gray-800 mb-2">{{ batch.volume.title }}</h3>
                                <p class="text-sm text-gray-600 font-maax leading-relaxed mb-2">{{ batch.volume.description }}</p>
                                <ul v-if="batch.volume.bottles && batch.volume.bottles.length" class="list-disc list-inside text-sm text-gray-600 space-y-1 font-maax">
                                    <li v-for="(bottle, index) in batch.volume.bottles" :key="index">{{ bottle }}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
        <Footer class="relative" />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import MainNav from '../components/MainNav.vue'
import Footer from '../components/Footer.vue'
import { batches } from '../data/batches.js'

const selectedBatches = ref([])

const toggleBatch = (batchId) => {
    const index = selectedBatches.value.indexOf(batchId)
    if (index > -1) {
        selectedBatches.value.splice(index, 1)
    } else {
        selectedBatches.value.push(batchId)
    }
}

const clearSelection = () => {
    selectedBatches.value = []
}

const filteredBatches = computed(() => {
    if (selectedBatches.value.length === 0) {
        return batches
    }
    return batches.filter(batch => selectedBatches.value.includes(batch.id))
})
</script>

<style>
html {
    scroll-behavior: smooth;
}
/* Optional: Add smooth transition for header */
header {
    transition: transform 0.3s ease;
}
</style> 